<script setup>
import { ref, computed, onBeforeUnmount, watch } from 'vue'
import {debounce} from 'lodash'
import { useStore } from 'vuex'
import { area, length } from '@turf/turf'
import {bbox} from '@turf/turf'

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Spinner } from '@/components/ui/spinner'
import {X, FilePlus2, Brush} from 'lucide-vue-next'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {parseKML, parseKMZ, parseGeoJSON} from '@/composables/files'
import axios from 'axios'
import FeatureSelectionItem from './FeatureSelectionItem.vue'

const props = defineProps({
  selectedGeometry: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:selectedGeometry'])

const store = useStore()
const isMapReady = computed(() => store.getters['geo/getMapReady'])
const map = computed(() => store.getters['geo/getMap'])
const draw = computed(() => store.getters['geo/getDraw'])

const previewGeometry = ref(null)
const isSearching = ref(false)
const searchText = ref('')
const searchFeatures = ref([])
const featureFileInput = ref(null)

const resetSearch = () => {
  const selected = props.selectedGeometry;
  searchFeatures.value = [];

  if (!selected) {
    emit('update:selectedGeometry', null);
  } else if (!searchFeatures.value.includes(selected)) {
    searchFeatures.value.push(selected);
  }
  previewGeometry.value = null;
}

const searchLocation = async (value) => {
  searchFeatures.value = [];

  if (props.selectedGeometry) {
    searchFeatures.value.push(props.selectedGeometry);
  }

  try {
    const response = await axios.get('https://nominatim.openstreetmap.org/search', {
      params: {
        q: value,
        polygon_geojson: 1,
        format: 'json',
        limit: 10
      }
    })

    const results = response.data

    if (results.length === 0) {
      return
    }

    for (const result of results) {
      if (result.geojson.type === 'Point' || result.geojson.type === 'LineString') {
        continue
      }
      const feature = {
        type: 'Feature',
        geometry: result.geojson,
        properties: {
          isSearch: true,
          name: result.display_name || result.name,
          addresstype: result.addresstype,
          placeId: result.place_id,
        }
      }

      searchFeatures.value.push(feature)
    }
  } catch (error) {
    console.error(error)
  }
}

const showFeature = (feature) => {
  previewGeometry.value = feature
}

const search = debounce(async () => {
  isSearching.value = true
  await searchLocation(searchText.value)
  isSearching.value = false
}, 1000)

const handleFileInputClick = () => {
  featureFileInput.value.click()
}

const handleFile = async (e) => {
  const files = e.target.files
  if (!files) return
  if (files.length === 0) return

  const file = files[0]
  const ext = file.name.split('.').pop().toLowerCase()
  let collection, name

  switch (ext) {
    case 'kml':
      ({ collection, name } = await parseKML(file))
      break
    case 'kmz':
      ({ collection, name } = await parseKMZ(file))
      break
    case 'geojson':
      ({ collection, name } = await parseGeoJSON(file))
      break
    default:
      toast({ title: 'Invalid file type', status: 'error' })
      break
  }

  if (!collection) {
    return
  }

  const features = collection.features
  if (features.length === 0) {
    toast({ title: 'No features found in file', status: 'error' })
    return
  }

  for (const feature of features) {
    feature.properties.isFromFile = true
    searchFeatures.value.push(feature)
  }
  emit('update:selectedGeometry', features[0])
}

const resetFiles = () => {
  if (featureFileInput.value) {
    featureFileInput.value.value = ''
  }
}

const selectGeometry = async (feature) => {
  if (!feature) {
    emit('update:selectedGeometry', null)
    return
  }
  emit('update:selectedGeometry', feature)

  if (feature.geometry) {
    const bounds = bbox(feature.geometry)
    map.value.fitBounds(bounds, {
      padding: 50,
      duration: 1000
    })
  }
}

const startDrawing = () => {
  draw.value.changeMode('draw_polygon')
}

const onDrawCreate = (e) => {
  const features = e.features
  for (const feature of features) {
    let areaText = ''

    if (feature.geometry.type === 'Polygon') {
      const areaInSm = area(feature)
      const areaInHa = (areaInSm / 10000).toFixed(3) // 1 ha = 10000 sm
      const areaInSkm = (areaInSm / 1000000).toFixed(4) // 1 skm = 1000000 sm
      areaText = `${areaInHa} ha (${areaInSkm} km²)`
    } else if (feature.geometry.type === 'LineString') {
      const lengthInM = length(feature, { units: 'meters' })
      const lengthInKm = (lengthInM / 1000).toFixed(2)
      areaText = `${lengthInKm} km`
    }

    feature.properties = {
      ...feature.properties,
      isDrawn: true,
      areaText
    }
    searchFeatures.value.push(feature)
  }
  if (features.length > 0) {
    emit('update:selectedGeometry', features[0])
  }
}

const onDrawUpdate = (e) => {
  const features = e.features
  for (const feature of features) {
    let areaText = ''

    if (feature.geometry.type === 'Polygon') {
      const areaInSm = area(feature)
      const areaInHa = (areaInSm / 10000).toFixed(3)
      const areaInSkm = (areaInSm / 1000000).toFixed(4)
      areaText = `${areaInHa} ha (${areaInSkm} km²)`
    } else if (feature.geometry.type === 'LineString') {
      const lengthInM = length(feature, { units: 'meters' })
      const lengthInKm = (lengthInM / 1000).toFixed(2)
      areaText = `${lengthInKm} km`
    }

    feature.properties = {
      ...feature.properties,
      isDrawn: true,
      areaText
    }

    const index = searchFeatures.value.findIndex(f => f.id === feature.id)
    if (index !== -1) {
      searchFeatures.value[index] = feature
    }
  }
}

const onDrawDelete = (e) => {
  const features = e.features
  for (const feature of features) {
    const index = searchFeatures.value.findIndex(f => f.id === feature.id)
    if (index !== -1) {
      searchFeatures.value.splice(index, 1)
    }
  }
  if (searchFeatures.value.length === 0) {
    emit('update:selectedGeometry', null)
  }
}

const deleteFeature = (feature) => {
  const index = searchFeatures.value.findIndex(f => f === feature);
  if (index !== -1) {
    searchFeatures.value.splice(index, 1)
    emit('update:selectedGeometry', null)
  }
}

watch(() => searchText.value, async (value) => {
  if (searchText.value.length === 0) {
    searchFeatures.value = [];
    if (props.selectedGeometry) {
      searchFeatures.value.push(props.selectedGeometry);
    } else {
      emit('update:selectedGeometry', null);
    }
    previewGeometry.value = null;
    return;
  }

  await search();
})

watch(() => isMapReady.value, (ready) => {
  if (ready && map.value) {
    map.value.on('draw.create', onDrawCreate)
    map.value.on('draw.update', onDrawUpdate)
    map.value.on('draw.delete', onDrawDelete)
  }
}, { immediate: true })

watch(() => props.selectedGeometry, (newGeometry) => {
  searchFeatures.value = searchFeatures.value.filter(f =>
    f !== props.selectedGeometry &&
    (!f.id || !newGeometry || f.id !== newGeometry.id)
  )

  if (newGeometry) {
    const feature = { ...newGeometry }

    if (!feature.properties) {
      feature.properties = {}
    }

    if (!feature.properties.name) {
      feature.properties.name = "Selected Area"
    }

    if (feature.geometry && !feature.geometry.type) {
      feature.geometry.type = "Polygon"
    }

    if (feature.geometry && feature.geometry.type === 'Polygon' && !feature.properties.areaText) {
      try {
        const areaInSm = area(feature)
        const areaInHa = (areaInSm / 10000).toFixed(3)
        const areaInSkm = (areaInSm / 1000000).toFixed(4)
        feature.properties.areaText = `${areaInHa} ha (${areaInSkm} km²)`
      } catch (error) {
        console.error('Error calculating area:', error)
      }
    }

    searchFeatures.value.push(feature)
  }
}, { immediate: true })

onBeforeUnmount(() => {
  if (map.value) {
    map.value.off('draw.create', onDrawCreate)
    map.value.off('draw.update', onDrawUpdate)
    map.value.off('draw.delete', onDrawDelete)
  }
  // Don't reset selected geometry on unmount
  searchFeatures.value = []
  previewGeometry.value = null
})
</script>

<template>
  <div>
    <div class="relative">
      <Input
        placeholder="Search for country, city or place"
        v-model="searchText"
      />
      <Button
        v-if="searchText.length > 0 && !isSearching"
        variant="ghost"
        class="p-0 w-6 h-6 absolute top-2 right-2"
        @click="searchText = ''"
      >
        <X class="w-4 h-4 text-gray-500" />
      </Button>
      <Spinner v-if="isSearching" class="w-4 h-4 absolute top-3 right-3" />
    </div>

    <div class="flex items-center">
      <div class="flex justify-center mt-2">
        <Button @click="handleFileInputClick" size="sm" variant="secondary" type="button">
          <FilePlus2 class="w-4 h-4 mr-2" />
          <span>Open</span>
        </Button>
        <input
          ref="featureFileInput"
          type="file"
          accept=".shp, .kml, .kmz, .geojson"
          @change="handleFile($event)"
          @click="resetFiles()"
          style="visibility: hidden; position: absolute; top: 0; left: 0; pointer-events: none;"
        />
      </div>

      <div class="flex justify-center mt-2">
        <Button @click="startDrawing" size="sm" variant="secondary" type="button" class="ml-2">
          <Brush class="w-4 h-4 mr-2" />
          <span>Draw</span>
        </Button>
      </div>
    </div>

    <FormField v-slot="{ value, componentField }" name="placeDetails">
      <FormItem>
        <FormControl>
          <div v-if="searchFeatures.length > 0" class="mt-2">
            <div class="bg-white border border-gray-200 rounded-md overflow-hidden">
              <FeatureSelectionItem
                v-for="(feature, index) in searchFeatures"
                :key="index"
                :feature="feature"
                :index="index"
                :map="map"
                :is-selected="selectedGeometry === feature"
                @select="selectGeometry"
                @delete="deleteFeature"
              />
            </div>
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>
  </div>
</template>
